import React from "react";
import type { Attachment } from "@services/drybulkCargoOperationsApi";
import { useTranslation } from "react-i18next";
import { DRYBULK_CARGO_OPS_LANG_BASE } from "../constants";
import { getAttachmentFileName } from "../utils/attachmentFileName";
import AttachmentFileCard from "./AttachmentFileCard";

const L = `${DRYBULK_CARGO_OPS_LANG_BASE}.documents`;

interface OperationAttachmentsListProps {
  attachments: Attachment[];
  isDownloading: (documentUrl: string) => boolean;
  onView: (attachment: Attachment) => void;
  onDownload: (attachment: Attachment) => void;
}

/**
 * DBCO-0030 (Q3) - one file card per attachment, in the order the server returned them (oldest first). The caption
 * is the type label; a type the SPA does not know shows as returned.
 */
const OperationAttachmentsList: React.FC<OperationAttachmentsListProps> = ({
  attachments,
  isDownloading,
  onView,
  onDownload,
}) => {
  const { t } = useTranslation();

  const captionFor = (attachment: Attachment) => {
    const typeLabel = t(`${L}.types.${attachment.attachmentType}`, {
      defaultValue: attachment.attachmentType,
    });
    return attachment.messageId ? `${typeLabel} · ${t(`${L}.from-messages`)}` : typeLabel;
  };

  if (attachments.length === 0) {
    return (
      <p className="text-sm text-gray-500" data-testid="drybulk-documents-empty">
        {t(`${L}.empty`)}
      </p>
    );
  }

  return (
    <ul className="space-y-3" data-testid="drybulk-documents-list">
      {attachments.map((attachment) => (
        <li key={attachment.id}>
          <AttachmentFileCard
            fileName={getAttachmentFileName(attachment)}
            caption={captionFor(attachment)}
            isDownloading={isDownloading(attachment.documentUrl)}
            onView={() => onView(attachment)}
            onDownload={() => onDownload(attachment)}
            testId={`drybulk-documents-attachment-${attachment.id}`}
          />
        </li>
      ))}
    </ul>
  );
};

export default OperationAttachmentsList;
